import { useTheme } from '../../theme';

export interface ToastProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Mensagem exibida (ex.: erro de login, aviso de vídeo) */
  message: string;
  /** Variante visual */
  variant?: 'error' | 'info' | 'success';
  /** Estilo adicional no container */
  style?: React.CSSProperties;
  /** role a11y (default: "alert" para erro) */
  role?: 'alert' | 'status';
}

export default function Toast({
  message,
  variant = 'info',
  style,
  role = variant === 'error' ? 'alert' : 'status',
  ...rest
}: ToastProps) {
  const theme = useTheme();
  const { palette, typography, shape, spacing } = theme;

  const baseStyle: React.CSSProperties = {
    position: 'fixed',
    top: spacing.lg,
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 9999,
    maxWidth: 'min(90vw, 420px)',
    padding: `${spacing.sm} ${spacing.md}`,
    borderRadius: shape.borderRadius.md,
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.medium,
    lineHeight: typography.lineHeight.normal,
    boxSizing: 'border-box',
    boxShadow: '0 4px 20px rgba(28, 43, 57, 0.15), 0 2px 8px rgba(0,0,0,0.08)',
    fontFamily: typography.fontFamily,
    display: 'flex',
    alignItems: 'center',
    gap: spacing.sm,
    animation: 'toastIn 0.25s ease-out',
  };

  const variantStyles: Record<string, React.CSSProperties> = {
    error: {
      backgroundColor: palette.background.paper,
      color: palette.error.main,
      border: `1px solid ${palette.error.main}`,
    },
    info: {
      backgroundColor: palette.background.paper,
      color: palette.text.primary,
      border: `1px solid ${palette.border.main}`,
      // Destaque sutil com a cor primária do projeto
      borderLeftWidth: '4px',
      borderLeftColor: palette.primary.main,
    },
    success: {
      backgroundColor: palette.background.paper,
      color: palette.text.primary,
      border: `1px solid ${palette.border.main}`,
      borderLeftWidth: '4px',
      borderLeftColor: '#059669',
    },
  };

  return (
    <>
      <style>{`
        @keyframes toastIn {
          from {
            opacity: 0;
            transform: translateX(-50%) translateY(-8px);
          }
          to {
            opacity: 1;
            transform: translateX(-50%) translateY(0);
          }
        }
      `}</style>
      <div
        role={role}
        style={{ ...baseStyle, ...variantStyles[variant], ...style }}
        aria-live="assertive"
        {...rest}
      >
        {message}
      </div>
    </>
  );
}
